import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { membersAPI, beneficiariesAPI, feeConfigAPI, paymentsAPI, paymentTransAPI, invoiceAPI, zonesAPI, paymentSlipsAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { InviteToPayModal } from './PendingAdmissions';
import { FIRM_CATEGORIES, DobField } from '../member/Profile';

function isSeniorExempt(dob) {
  if (!dob || new Date() < new Date('2026-07-01')) return false;
  const d = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  if (today < new Date(today.getFullYear(), d.getMonth(), d.getDate())) age--;
  return age >= 66;
}

const TABS = [
  { key: 'summary',       label: 'Overview' },
  { key: 'personal',      label: 'Personal' },
  { key: 'membership',    label: 'Membership' },
  { key: 'firm',          label: 'Firm / Institution' },
  { key: 'beneficiaries', label: 'Beneficiaries' },
  { key: 'payments',      label: 'Payments' },
];

const initialForm = {
  full_name: '', roll_no: '', email: '', phone: '', gender: '',
  date_of_birth: '', date_of_death: '', chapter: '', admission_date: '',
  specialization: '', position: '', status: '', firm_name: '', firm_address: '',
  firm_location: '', firm_tin: '', firm_category: '',
};

export default function MemberDetail() {
  const { id }    = useParams();
  const { user }  = useAuthStore();
  const [activeTab, setActiveTab] = useState('summary');
  const statusField = useRef(null);
  const [focusStatus, setFocusStatus] = useState(false);

  useEffect(() => {
    if (activeTab === 'membership' && focusStatus) {
      statusField.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      statusField.current?.focus({ preventScroll: true });
      setFocusStatus(false);
    }
  }, [activeTab, focusStatus]);

  const [member, setMember]       = useState(null);
  const [formData, setFormData]   = useState(initialForm);
  const [statusOptions, setStatusOptions] = useState([
    'DECEASED', 'NON-PRACTISING MEMBER', 'NOTARY MEMBER', 'PRACTISING MEMBER',
  ]);

  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [zones, setZones]         = useState([]);

  // Beneficiaries
  const [beneficiaries, setBeneficiaries]         = useState([]);
  const [ben1, setBen1]                           = useState(null); // selected_installment_one_beneficiary
  const [ben2, setBen2]                           = useState(null); // selected_installment_two_beneficiary
  const [bensLoading, setBensLoading]             = useState(false);
  const [bensLoaded, setBensLoaded]               = useState(false);
  const [payoutConfig, setPayoutConfig]           = useState(null);

  // Outstanding debt
  const [outstanding, setOutstanding]         = useState(null);
  const [outstandingLoading, setOutstandingLoading] = useState(false);

  // Payments tab
  const [invoices, setInvoices]             = useState([]);
  const [transactions, setTransactions]     = useState([]);
  const [paymentSlips, setPaymentSlips]     = useState([]);
  const [markPaidBusy, setMarkPaidBusy]     = useState(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsLoaded, setPaymentsLoaded]   = useState(false);
  const [selectedPayYear, setSelectedPayYear] = useState(new Date().getFullYear());

  async function handleMarkPaid(invoiceId) {
    if (!confirm('Confirm you have verified the deposit slip and this invoice is paid?')) return;
    setMarkPaidBusy(invoiceId);
    try {
      await paymentsAPI.markAsPaid(invoiceId);
      const [payRes, transRes] = await Promise.all([
        paymentsAPI.getByMember(id),
        paymentTransAPI.getByMember(id),
      ]);
      setInvoices(payRes.invoices || []);
      setTransactions(transRes.transactions || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to mark as paid.');
    } finally {
      setMarkPaidBusy(null);
    }
  }

  useEffect(() => {
    Promise.all([
      membersAPI.get(id),
      membersAPI.statuses().catch(() => null),
      zonesAPI.list().catch(() => ({ zones: [] })),
    ]).then(([resp, statuses, zonesResp]) => {
      setMember(resp);
      setFormData({
        full_name:      resp.full_name      || '',
        roll_no:        resp.roll_no        || '',
        email:          resp.email          || '',
        phone:          resp.phone          || '',
        gender:         resp.gender         || '',
        date_of_birth:  resp.date_of_birth  || '',
        date_of_death:  resp.date_of_death  || '',
        chapter:        resp.chapter        || '',
        admission_date: resp.admission_date || '',
        specialization: resp.specialization || '',
        position:       resp.position       || '',
        status:         resp.status         || '',
        firm_name:      resp.firm_name      || '',
        firm_address:   resp.firm_address   || '',
        firm_location:  resp.firm_location  || '',
        firm_tin:       resp.firm_tin       || '',
        firm_category:  resp.firm_category  || '',
      });
      if (statuses?.statuses?.length) setStatusOptions(statuses.statuses);
      setZones(zonesResp?.zones || []);
    }).catch((e) => {
      setError(e.response?.data?.error || 'Failed to load member.');
    }).finally(() => setLoading(false));
  }, [id]);

  // Fetch outstanding debt once member is loaded
  useEffect(() => {
    if (!member?.id) return;
    setOutstandingLoading(true);
    membersAPI.outstanding(member.id)
      .then(setOutstanding)
      .catch(() => setOutstanding(null))
      .finally(() => setOutstandingLoading(false));
  }, [member?.id]);

  // Load payments when tab is first opened
  useEffect(() => {
    if (activeTab !== 'payments' || paymentsLoaded) return;
    setPaymentsLoading(true);
    Promise.all([
      paymentsAPI.getByMember(id),
      paymentTransAPI.getByMember(id),
      paymentSlipsAPI.getByMember(id).catch(() => ({ slips: [] })),
    ]).then(([payRes, transRes, slipsRes]) => {
      setInvoices(payRes.invoices || []);
      setTransactions(transRes.transactions || []);
      setPaymentSlips(slipsRes.slips || []);
    }).catch(() => {})
      .finally(() => { setPaymentsLoading(false); setPaymentsLoaded(true); });
  }, [activeTab, id, paymentsLoaded]);

  // Load beneficiaries + payout config when tab is first opened
  useEffect(() => {
    if (activeTab !== 'beneficiaries' || bensLoaded) return;
    setBensLoading(true);
    Promise.all([
      beneficiariesAPI.getByMember(id),
      feeConfigAPI.getAll().catch(() => null),
    ]).then(([res, feeRes]) => {
      setBeneficiaries(Array.isArray(res) ? res : (res.beneficiaries || []));
      setBen1(res.selected_installment_one_beneficiary || null);
      setBen2(res.selected_installment_two_beneficiary || null);
      if (feeRes?.beneficiary_payout) setPayoutConfig(feeRes.beneficiary_payout);
    })
      .catch(() => setBeneficiaries([]))
      .finally(() => { setBensLoading(false); setBensLoaded(true); });
  }, [activeTab, id, bensLoaded]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'status' && value.toLowerCase() !== 'deceased' ? { date_of_death: '' } : {}),
    }));
  };

  const selectedZone = zones.find((z) => z.chapters.some((c) => c.name === formData.chapter));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      const changes = Object.fromEntries(
        Object.entries(formData).filter(([key, value]) => value !== (member[key] ?? ''))
      );
      if (Object.keys(changes).length === 0) {
        setSuccess('No changes to save.');
        return;
      }
      await membersAPI.update(id, changes);
      const refreshed = await membersAPI.get(id);
      setMember(refreshed);
      setSuccess('Member updated successfully.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update member.');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = async () => {
    if (!window.confirm(`Unlock ${member?.full_name}'s account so they can log in again now?`)) return;
    setUnlocking(true); setError(''); setSuccess('');
    try {
      await membersAPI.unlockAccount(id);
      const refreshed = await membersAPI.get(id);
      setMember(refreshed);
      setSuccess('Account unlocked — the member can log in again now.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unlock account.');
    } finally {
      setUnlocking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        Loading member details…
      </div>
    );
  }

  const isAdmin = user?.role === 'admin';
  const canChangeStatus = isAdmin || (user?.permissions || []).includes('members') || (user?.permissions || []).includes('member_status');
  const canInvite = isAdmin || (user?.permissions || []).includes('members') || (user?.permissions || []).includes('member_invite');
  const canManageAccount = isAdmin || (user?.permissions || []).includes('members');
  const showDeathDate = formData.status?.toLowerCase() === 'deceased';

  return (
    <div className="space-y-5">

      {/* Back */}
      <Link
        to="/members"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors"
      >
        <BackIcon /> Back to Members
      </Link>

      {/* Hero header */}
      <div className="rounded-2xl bg-[linear-gradient(135deg,#0f172a,#f07415)] px-6 py-6 text-white shadow-lg">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 shrink-0 rounded-2xl overflow-hidden bg-white/20 backdrop-blur-sm flex items-center justify-center">
              {member?.picture ? (
                <img
                  src={member.picture}
                  alt={member.full_name}
                  className="h-full w-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
              ) : null}
              <span className={`text-xl font-black text-white ${member?.picture ? 'hidden' : 'flex'} items-center justify-center h-full w-full`}>
                {(member?.full_name || '?')[0].toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-orange-200/70 mb-1">Member Profile</p>
              <h1 className="text-xl font-bold leading-tight sm:text-2xl">{member?.full_name || '—'}</h1>
              <p className="mt-1 text-sm text-orange-100/70">{member?.email || ''}</p>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Chip label={member?.roll_no ? 'Roll No' : 'Petition No'} value={member?.roll_no || member?.petition_no || '—'} />
            <Chip label="Status" value={member?.status || 'Unknown'} />
            {canChangeStatus && <button type="button" onClick={() => { setActiveTab('membership'); setFocusStatus(true); }}
              className="inline-flex items-center rounded-full bg-white px-4 py-2 text-xs font-bold text-orange-700 shadow hover:bg-orange-50 focus-visible:ring-2 focus-visible:ring-white">
              Change Status
            </button>}
            <Chip label="Chapter" value={member?.chapter || '—'} />
            {isSeniorExempt(member?.date_of_birth) && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/25 px-3 py-1 text-xs font-bold text-emerald-100 ring-1 ring-emerald-300/30">
                🎖 Fee Exempt (Age 66+)
              </span>
            )}
            {canInvite && !member?.user_id && (
              <button onClick={() => setShowInvite(true)}
                className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-1.5 text-xs font-bold text-orange-700 shadow hover:bg-orange-50 transition-colors">
                {member?.status === 'PENDING_ADMISSION' ? 'Invite to Pay' : 'Invite'}
              </button>
            )}
            {canInvite && member?.user_id && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/25 px-4 py-1.5 text-xs font-bold text-emerald-100 ring-1 ring-emerald-300/30">
                ✓ Has Account
              </span>
            )}
            {member?.account_locked && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/90 px-3 py-1 text-xs font-bold text-white ring-1 ring-red-300/40">
                🔒 Account Locked
              </span>
            )}
            {canManageAccount && member?.account_locked && (
              <button onClick={handleUnlock} disabled={unlocking}
                className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-1.5 text-xs font-bold text-red-600 shadow hover:bg-red-50 transition-colors disabled:opacity-60">
                {unlocking ? 'Unlocking…' : '🔓 Unlock Account'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error   && <Alert type="error"   text={error}   />}
      {success && <Alert type="success" text={success} />}

      {/* Tab bar */}
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-0">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { setActiveTab(t.key); setError(''); setSuccess(''); }}
            className={`rounded-t-lg px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === t.key
                ? 'border-orange-500 text-orange-600 bg-white'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
            {t.key === 'beneficiaries' && bensLoaded && beneficiaries.length > 0 && (
              <span className="ml-1.5 rounded-full bg-orange-100 px-1.5 py-0.5 text-xs font-semibold text-orange-600">
                {beneficiaries.length}
              </span>
            )}
            {t.key === 'payments' && paymentsLoaded && invoices.length > 0 && (
              <span className="ml-1.5 rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-600">
                {invoices.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6">

        {/* ── Overview ── */}
        {activeTab === 'summary' && (
          <div className="grid gap-6 lg:grid-cols-2">
            <section className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Personal</h2>
                {isAdmin && (
                  <button type="button" onClick={() => { setActiveTab('personal'); setError(''); setSuccess(''); }}
                    className="rounded-lg bg-orange-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-orange-500">
                    Edit Personal Details
                  </button>
                )}
              </div>
              <InfoGrid>
                <InfoRow label="Full Name"    value={member?.full_name    || '—'} />
                <InfoRow label={member?.roll_no ? 'Roll No' : 'Petition No'} value={member?.roll_no || member?.petition_no || '—'} />
                <InfoRow label="Email"        value={member?.email        || '—'} />
                <InfoRow label="Phone"        value={member?.phone        || '—'} />
                <InfoRow label="Gender"       value={member?.gender       || '—'} />
                <InfoRow label="Date of Birth" value={member?.date_of_birth || '—'} />
                <InfoRow label="Chapter"      value={member?.chapter      || '—'} />
              </InfoGrid>
            </section>

            <section className="space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Membership & Firm</h2>
              <InfoGrid>
                <InfoRow label="Status"         value={member?.status         || '—'} />
                <InfoRow label="Admission Date" value={member?.admission_date || '—'} />
                <InfoRow label="Position"       value={member?.position       || '—'} />
                <InfoRow label="Specialization" value={member?.specialization || '—'} />
                {member?.date_of_death && (
                  <InfoRow label="Date of Death" value={member.date_of_death} />
                )}
                <InfoRow label="Firm / Institution" value={member?.firm_name  || '—'} />
                <InfoRow label="Firm Location"  value={member?.firm_location  || '—'} />
                <InfoRow label="Firm TIN"       value={member?.firm_tin       || '—'} />
                <InfoRow label="Firm Category"  value={member?.firm_category  || '—'} />
              </InfoGrid>

              {/* Life membership status */}
              {member?.membership_type === 'life' && (
                <div className="mt-3 rounded-2xl border-2 border-violet-300 bg-violet-50 px-4 py-3 flex items-center gap-3">
                  <div className="h-9 w-9 shrink-0 rounded-full bg-violet-600 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="h-5 w-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-violet-800">Life Member</p>
                    <p className="text-xs text-violet-600">Enrolled: {member.life_member_date || '—'} · Exempt from annual contributions</p>
                  </div>
                </div>
              )}

              {/* Annual fees arrears */}
              {!outstandingLoading && outstanding && !outstanding.exempt && (
                <OutstandingDebtCard data={outstanding} memberId={member.id} />
              )}
            </section>
          </div>
        )}

        {/* ── Edit forms ── */}
        {['personal', 'membership', 'firm'].includes(activeTab) && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {activeTab === 'personal' && (
              <>
                <SectionHead title="Personal Information" sub="Core identity and contact details." />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Full Name"     name="full_name"     value={formData.full_name}     onChange={handleChange} disabled={!isAdmin} />
                  <Field label="Roll No"       name="roll_no"       value={formData.roll_no}       onChange={handleChange} disabled={!isAdmin} />
                  <Field label="Email"         name="email"         type="email" value={formData.email}  onChange={handleChange} disabled={!isAdmin} />
                  <Field label="Phone"         name="phone"         type="tel" value={formData.phone}         onChange={handleChange} disabled={!isAdmin} />
                  <Field label="Gender"        name="gender"        value={formData.gender}        onChange={handleChange} disabled={!isAdmin} />
                  <DobField value={formData.date_of_birth} onChange={handleChange} disabled={!isAdmin} />
                </div>
              </>
            )}

            {activeTab === 'membership' && (
              <>
                <SectionHead title="Membership Information" sub="Chapter, admission, role and status." />
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Chapter</label>
                    <select name="chapter" value={formData.chapter} onChange={handleChange} disabled={!isAdmin}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-slate-50">
                      <option value="">Select chapter</option>
                      {zones.map((z) => (
                        <optgroup key={z.id} label={z.name}>
                          {z.chapters.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                        </optgroup>
                      ))}
                    </select>
                    {selectedZone && (
                      <p className="mt-1.5 text-xs font-medium text-orange-600">Zone: {selectedZone.name}</p>
                    )}
                  </div>
                  <Field label="Admission Date" name="admission_date" type="date" value={formData.admission_date} onChange={handleChange} disabled={!isAdmin} />
                  <Field label="Specialization" name="specialization" value={formData.specialization} onChange={handleChange} disabled={!isAdmin} />
                  <Field label="Position"       name="position"       value={formData.position}       onChange={handleChange} disabled={!isAdmin} />
                  <div>
                    <label htmlFor="member-status" className="block text-xs font-medium text-slate-700 mb-1.5">Status</label>
                    <select
                      id="member-status" ref={statusField} name="status" value={formData.status} onChange={handleChange} disabled={!canChangeStatus}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-slate-50"
                    >
                      <option value="">Select status</option>
                      {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  {showDeathDate && (
                    <Field label="Date of Death" name="date_of_death" type="date" value={formData.date_of_death} onChange={handleChange} disabled={!isAdmin} />
                  )}
                </div>
                {showDeathDate && !formData.date_of_death && (
                  <p className="text-sm text-rose-600">Please enter the date of death for a deceased member.</p>
                )}
              </>
            )}

            {activeTab === 'firm' && (
              <>
                <SectionHead title="Firm / Institution" sub="Workplace details." />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Firm / Institution Name" name="firm_name"     value={formData.firm_name}     onChange={handleChange} disabled={!isAdmin} />
                  <Field label="Location"                name="firm_location" value={formData.firm_location} onChange={handleChange} disabled={!isAdmin} />
                  <Field label="TIN"                     name="firm_tin"      value={formData.firm_tin}      onChange={handleChange} disabled={!isAdmin} />
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Category</label>
                    <select name="firm_category" value={formData.firm_category} onChange={handleChange} disabled={!isAdmin}
                      className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-slate-50 disabled:text-slate-500">
                      <option value="">Select category…</option>
                      {FIRM_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Address</label>
                    <textarea
                      name="firm_address" value={formData.firm_address} onChange={handleChange}
                      rows={3} disabled={!isAdmin}
                      className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-slate-50"
                    />
                  </div>
                </div>
              </>
            )}

            {isAdmin && (
              <div>
                <button
                  type="submit" disabled={saving}
                  className="rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white font-semibold px-6 py-2.5 text-sm transition-colors"
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            )}
          </form>
        )}

        {/* ── Payments ── */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            {paymentsLoading ? (
              <p className="text-slate-400 text-sm py-10 text-center">Loading payments…</p>
            ) : (() => {
              const THIS_YEAR = new Date().getFullYear();
              const availableYears = [...new Set([
                THIS_YEAR,
                ...invoices.map(i => Number(i.fee_year)),
              ])].sort((a, b) => b - a);

              const yearIdx         = availableYears.indexOf(selectedPayYear);
              const yearInvoices    = invoices.filter(i => Number(i.fee_year) === selectedPayYear);
              // Link transactions to invoices by reference_no (same TLSD ref in both tables)
              const yearInvRefs     = new Set(yearInvoices.map(i => i.reference_no).filter(Boolean));
              const yearTrans       = transactions.filter(t => yearInvRefs.has(t.reference_no));
              const yearPaid        = yearInvoices.filter(i => String(i.paystatus || '').toUpperCase() === 'PAID').reduce((s, i) => s + Number(i.fee || 0), 0);
              const yearInvoiceOutstanding = yearInvoices.filter(i => String(i.paystatus || '').toUpperCase() !== 'PAID').reduce((s, i) => s + Number(i.fee || 0), 0);
              // No invoice has been raised for this year yet (e.g. a member admitted
              // this year who hasn't self-generated their invoice) — fall back to the
              // estimated fee for that year so the admin still sees what's owed,
              // instead of a misleading TZS 0.
              const estimatedYearDebt = outstanding?.years?.find(y => Number(y.year) === selectedPayYear)?.amount || 0;
              const yearOutstanding   = yearInvoices.length > 0 ? yearInvoiceOutstanding : estimatedYearDebt;
              const isEstimate        = yearInvoices.length === 0 && estimatedYearDebt > 0;

              return (
                <>
                  {/* Year navigation */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-1 py-1">
                      <button
                        onClick={() => yearIdx < availableYears.length - 1 && setSelectedPayYear(availableYears[yearIdx + 1])}
                        disabled={yearIdx >= availableYears.length - 1}
                        className="flex h-7 w-7 items-center justify-center rounded-full text-slate-500 hover:bg-white hover:text-slate-800 disabled:opacity-30 font-bold text-lg transition-colors"
                      >
                        ‹
                      </button>
                      <span className="min-w-[52px] text-center text-sm font-bold text-slate-800 select-none">
                        {selectedPayYear}
                      </span>
                      <button
                        onClick={() => yearIdx > 0 && setSelectedPayYear(availableYears[yearIdx - 1])}
                        disabled={yearIdx <= 0}
                        className="flex h-7 w-7 items-center justify-center rounded-full text-slate-500 hover:bg-white hover:text-slate-800 disabled:opacity-30 font-bold text-lg transition-colors"
                      >
                        ›
                      </button>
                    </div>
                    <p className="text-xs text-slate-400">
                      {availableYears.length} year{availableYears.length !== 1 ? 's' : ''} on record
                    </p>
                  </div>

                  {/* Summary for selected year */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Paid · {selectedPayYear}</p>
                      <p className="mt-1.5 text-xl font-bold text-emerald-900">
                        <span className="text-xs font-semibold text-emerald-600 mr-1">TZS</span>
                        {yearPaid.toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Outstanding · {selectedPayYear}</p>
                      <p className="mt-1.5 text-xl font-bold text-amber-900">
                        <span className="text-xs font-semibold text-amber-600 mr-1">TZS</span>
                        {yearOutstanding.toLocaleString()}
                      </p>
                      {isEstimate && (
                        <p className="mt-0.5 text-[10px] font-medium text-amber-500">Estimated · no invoice raised yet</p>
                      )}
                    </div>
                  </div>

                  {/* Invoice + Transaction cards */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">
                      Invoices — {yearInvoices.length}
                    </h3>
                    {yearInvoices.length === 0 ? (
                      <div className="text-center py-8 rounded-2xl border border-slate-100 bg-slate-50">
                        <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400 text-lg">📄</div>
                        <p className="text-slate-500 text-sm">No invoices for {selectedPayYear}.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {yearInvoices.map((inv) => {
                          const paid = String(inv.paystatus || '').toUpperCase() === 'PAID';
                          const trans = transactions.find(t => t.reference_no === inv.reference_no);
                          return (
                            <div key={inv.id} className={`rounded-2xl border overflow-hidden ${
                              paid ? 'border-emerald-200' : 'border-amber-200'
                            }`}>
                              {/* Invoice row */}
                              <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3">
                                <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-mono text-sm font-bold text-slate-800 tracking-wide">
                                      {inv.reference_no || '—'}
                                    </span>
                                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${
                                      paid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                                    }`}>
                                      {paid ? 'Paid' : 'Pending'}
                                    </span>
                                  </div>
                                  <p className="text-xs text-slate-400 mt-0.5">
                                    {inv.fee_type || 'Annual Fee'} · Year {inv.fee_year}
                                    {inv.duedate ? ` · Due ${inv.duedate}` : ''}
                                  </p>
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-[10px] text-slate-400 font-medium">TZS</p>
                                  <p className="text-base font-bold text-slate-800 leading-tight">
                                    {Number(inv.fee || 0).toLocaleString()}
                                  </p>
                                </div>
                              </div>

                              {/* CRDB transaction block */}
                              {paid && trans ? (
                                <div className="border-t border-emerald-100 bg-emerald-50/60 px-4 py-3">
                                  <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 mb-2">
                                    ✓ CRDB Payment
                                  </p>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                                    <div>
                                      <p className="text-slate-400">Date</p>
                                      <p className="font-medium text-slate-700">
                                        {trans.paid_at ? String(trans.paid_at).slice(0, 10) : '—'}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-slate-400">Amount (TZS)</p>
                                      <p className="font-bold text-emerald-700">
                                        {Number(trans.amount || 0).toLocaleString()}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-slate-400">Channel</p>
                                      <p className="font-medium text-slate-700">{trans.channel || '—'}</p>
                                    </div>
                                    <div>
                                      <p className="text-slate-400">Trans Ref</p>
                                      <p className="font-mono text-slate-600 truncate">{trans.trans_ref || '—'}</p>
                                    </div>
                                    {(trans.vfd_url || trans.vfdrcpt) && (
                                      <div className="col-span-2">
                                        <p className="text-slate-400">VFD</p>
                                        {trans.vfd_url ? (
                                          <a href={trans.vfd_url} target="_blank" rel="noreferrer"
                                            className="font-semibold text-blue-600 hover:underline">
                                            View Receipt
                                          </a>
                                        ) : (
                                          <span className="font-mono text-slate-600">{trans.vfdrcpt}</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : paid && !trans ? (
                                <div className="border-t border-amber-100 bg-amber-50/60 px-4 py-3 flex items-start gap-2">
                                  <span className="text-base shrink-0 mt-0.5">⚠️</span>
                                  <div>
                                    <p className="text-xs font-semibold text-amber-800">CRDB record not found</p>
                                    <p className="text-[11px] text-amber-700 mt-0.5">Invoice is paid but no matching CRDB transaction record found.</p>
                                  </div>
                                </div>
                              ) : null}

                              {/* Direct-deposit slip(s) attached by the member */}
                              {paymentSlips.filter(s => s.reference_nos === inv.reference_no).map(slip => (
                                <div key={slip.id} className="border-t border-blue-100 bg-blue-50/60 px-4 py-3">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="text-[10px] font-bold uppercase tracking-widest text-blue-600 mb-1">
                                        📎 Direct Deposit Slip
                                      </p>
                                      <p className="text-xs text-slate-600">
                                        {slip.date_paid || '—'} · TZS {Number(slip.amount || 0).toLocaleString()}
                                      </p>
                                      {slip.member_notes && (
                                        <p className="text-[11px] text-slate-500 mt-0.5">{slip.member_notes}</p>
                                      )}
                                      {slip.attachment_path && (
                                        <a href={slip.attachment_path} target="_blank" rel="noreferrer"
                                          className="mt-1 inline-block text-xs font-semibold text-blue-600 hover:underline">
                                          View Attachment →
                                        </a>
                                      )}
                                    </div>
                                    {!paid && (
                                      <button
                                        onClick={() => handleMarkPaid(inv.id)}
                                        disabled={markPaidBusy === inv.id}
                                        className="shrink-0 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white px-3 py-1.5 text-xs font-semibold transition-colors">
                                        {markPaidBusy === inv.id ? 'Saving…' : 'Mark as Paid'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}

                              {/* PDF buttons */}
                              <div className="flex items-center gap-2 px-4 py-2.5 border-t border-slate-100 bg-slate-50/50">
                                <InvBtn id={inv.id} type="invoice" />
                                {paid && <InvBtn id={inv.id} type="receipt" />}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ── Beneficiaries ── */}
        {activeTab === 'beneficiaries' && (
          <div className="space-y-6">
            {bensLoading ? (
              <p className="text-slate-400 text-sm py-10 text-center">Loading…</p>
            ) : beneficiaries.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400 text-xl">👤</div>
                <p className="text-slate-500 font-medium">No beneficiaries registered</p>
                <p className="text-xs text-slate-400 mt-1">This member has not added any next of kin yet.</p>
              </div>
            ) : (
              <>
                {/* ── Payout Summary ── */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Payout Assignment</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <PayoutCard
                      label="1st Beneficiary"
                      labelColor="text-emerald-700"
                      borderColor="border-emerald-300"
                      bgColor="bg-emerald-50"
                      iconBg="bg-emerald-600"
                      person={ben1}
                      amount={payoutConfig?.amount ?? (ben1 ? ben1.installment_one_amount : null)}
                    />
                    <PayoutCard
                      label="2nd Beneficiary"
                      labelColor="text-violet-700"
                      borderColor="border-violet-300"
                      bgColor="bg-violet-50"
                      iconBg="bg-violet-600"
                      person={ben2}
                      amount={ben2 ? ben2.installment_two_amount : null}
                    />
                  </div>
                </div>

                {/* ── Next of Kin List ── */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">
                    Next of Kin — {beneficiaries.length} {beneficiaries.length === 1 ? 'person' : 'people'}
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {beneficiaries.map((b) => {
                      const isBen1 = ben1?.id === b.id;
                      const isBen2 = ben2?.id === b.id;
                      return (
                        <div
                          key={b.id}
                          className={`rounded-2xl border bg-white shadow-sm overflow-hidden ${
                            isBen1 ? 'border-emerald-300' : isBen2 ? 'border-violet-300' : 'border-slate-200'
                          }`}
                        >
                          {/* Card header */}
                          <div className={`px-4 py-3 flex items-center gap-3 ${
                            isBen1 ? 'bg-emerald-50' : isBen2 ? 'bg-violet-50' : 'bg-slate-50'
                          }`}>
                            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white font-bold text-sm ${
                              isBen1 ? 'bg-emerald-600' : isBen2 ? 'bg-violet-600' : 'bg-slate-500'
                            }`}>
                              {(b.full_name || '?')[0].toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-slate-900 text-sm truncate">{b.full_name}</p>
                              <p className="text-xs text-slate-500 capitalize">{(b.relationship || '').toLowerCase()}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1 shrink-0">
                              {isBen1 && (
                                <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
                                  1st Beneficiary
                                </span>
                              )}
                              {isBen2 && (
                                <span className="text-[10px] font-bold uppercase tracking-wide text-violet-700 bg-violet-100 rounded-full px-2 py-0.5">
                                  2nd Beneficiary
                                </span>
                              )}
                              {!isBen1 && !isBen2 && (
                                <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
                                  Next of Kin
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Card body */}
                          <div className="px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-2.5">
                            <BenField label="Gender"        value={b.gender        || '—'} />
                            <BenField label="Phone"         value={b.phone         || '—'} />
                            <BenField label="Date of Birth" value={b.date_of_birth || '—'} />
                            <BenField label="Benefits"      value={b.benefits > 0 ? `${b.benefits}%` : '—'} />
                            {b.email && (
                              <div className="col-span-2">
                                <BenField label="Email" value={b.email} />
                              </div>
                            )}
                            {b.address && (
                              <div className="col-span-2">
                                <BenField label="Address" value={b.address} />
                              </div>
                            )}
                          </div>

                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {showInvite && member && (
        <InviteToPayModal
          record={{ member_id: member.id, full_name: member.full_name, email: member.email, phone: member.phone }}
          onClose={() => setShowInvite(false)}
          context={member.status === 'PENDING_ADMISSION' ? 'pending_admission' : 'general'}
        />
      )}
    </div>
  );
}

// ── Small components ──────────────────────────────────────────────────────────

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="m14.25 18-6-6 6-6" />
    </svg>
  );
}

function Chip({ label, value }) {
  return (
    <div className="rounded-xl border border-white/20 bg-white/10 px-3 py-2 backdrop-blur">
      <p className="text-[10px] uppercase tracking-widest text-orange-200/60">{label}</p>
      <p className="text-sm font-semibold mt-0.5">{value}</p>
    </div>
  );
}

function Alert({ type, text }) {
  const cls = type === 'error'
    ? 'border-red-200 bg-red-50 text-red-700'
    : 'border-emerald-200 bg-emerald-50 text-emerald-700';
  return <div className={`rounded-xl border px-4 py-3 text-sm ${cls}`}>{text}</div>;
}

function SectionHead({ title, sub }) {
  return (
    <div className="mb-2">
      <h2 className="text-base font-semibold text-slate-800">{title}</h2>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function InfoGrid({ children }) {
  return <div className="rounded-xl border border-slate-100 overflow-hidden divide-y divide-slate-100">{children}</div>;
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 bg-white hover:bg-slate-50 transition-colors">
      <span className="text-xs text-slate-500 font-medium shrink-0">{label}</span>
      <span className="text-xs font-semibold text-slate-800 text-right ml-4 break-all">{value}</span>
    </div>
  );
}

function BenField({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">{label}</p>
      <p className="text-xs font-semibold text-slate-700 mt-0.5 break-all">{value}</p>
    </div>
  );
}

function PayoutCard({ label, labelColor, borderColor, bgColor, iconBg, person, amount }) {
  return (
    <div className={`rounded-2xl border-2 ${borderColor} ${bgColor} p-4`}>
      <p className={`text-xs font-bold uppercase tracking-widest mb-3 ${labelColor}`}>{label}</p>
      {person ? (
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${iconBg} text-white font-bold text-sm`}>
            {(person.full_name || '?')[0].toUpperCase()}
          </div>
          <div>
            <p className="font-semibold text-slate-900 text-sm">{person.full_name}</p>
            <p className="text-xs text-slate-500 capitalize">{(person.relationship || '').toLowerCase()}</p>
            {amount && (
              <p className={`text-xs font-bold mt-0.5 ${labelColor}`}>
                TZS {Number(amount).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 opacity-50">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-300 text-white text-lg">?</div>
          <p className="text-sm text-slate-500">Not assigned</p>
        </div>
      )}
    </div>
  );
}

function Field({ label, name, type = 'text', value, onChange, disabled = false }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1.5">{label}</label>
      <input
        type={type} name={name} value={value} onChange={onChange} disabled={disabled}
        className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-slate-50 disabled:text-slate-500"
      />
    </div>
  );
}

function TransNoData({ hasPaid, year }) {
  if (hasPaid) {
    return (
      <div className="py-5 px-4 rounded-xl border border-amber-200 bg-amber-50 flex items-start gap-3">
        <span className="text-xl mt-0.5 shrink-0">⚠️</span>
        <div>
          <p className="text-sm font-semibold text-amber-800">Invoice paid — CRDB record not found</p>
          <p className="text-xs text-amber-700 mt-0.5">
            Payment recorded on the invoice but payment_trans has no matching record for this reference.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="text-center py-8 rounded-xl border border-slate-100 bg-slate-50">
      <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400 text-lg">💳</div>
      <p className="text-slate-500 text-sm">No payments received for {year}.</p>
    </div>
  );
}

function InvBtn({ id, type }) {
  const [busy, setBusy] = React.useState(false);
  async function handle() {
    setBusy(true);
    try {
      if (type === 'receipt') await invoiceAPI.openReceipt(id);
      else await invoiceAPI.openPdf(id);
    } catch {}
    finally { setBusy(false); }
  }
  return (
    <button
      onClick={handle}
      disabled={busy}
      className={`rounded-lg px-2.5 py-1 text-xs font-semibold disabled:opacity-50 transition-colors ${
        type === 'receipt'
          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
      }`}
    >
      {busy ? '…' : type === 'receipt' ? 'Receipt' : 'Invoice'}
    </button>
  );
}

function OutstandingDebtCard({ data, memberId }) {
  const { fees_year, total_debt, years } = data;
  const currentYear = new Date().getFullYear();

  if (!fees_year || fees_year >= currentYear) {
    return (
      <div className="mt-3 rounded-2xl border-2 border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-3">
        <div className="h-8 w-8 shrink-0 rounded-full bg-emerald-500 flex items-center justify-center">
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="h-4 w-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
          </svg>
        </div>
        <div>
          <p className="text-sm font-bold text-emerald-800">Fees Up to Date</p>
          <p className="text-xs text-emerald-600">Paid through {fees_year || currentYear}</p>
        </div>
      </div>
    );
  }

  if (total_debt === 0) {
    return (
      <div className="mt-3 rounded-2xl border-2 border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-3">
        <div className="h-8 w-8 shrink-0 rounded-full bg-emerald-500 flex items-center justify-center">
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="h-4 w-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
          </svg>
        </div>
        <div>
          <p className="text-sm font-bold text-emerald-800">No Outstanding Fees</p>
          <p className="text-xs text-emerald-600">All years covered by paid invoices</p>
        </div>
      </div>
    );
  }

  const [expanded, setExpanded] = React.useState(false);
  const [splitting, setSplitting] = React.useState(false);
  const [splitResult, setSplitResult] = React.useState(null);
  const eligibleForInstallment = years.length > 4;

  async function handleSplit() {
    if (!window.confirm(`Split this member's ${years.length}-year arrears (TZS ${Number(total_debt).toLocaleString()}) into 2 installment control numbers (50/50)?`)) {
      return;
    }
    setSplitting(true);
    setSplitResult(null);
    try {
      const res = await invoiceAPI.generateArrearsInstallment(memberId);
      setSplitResult({ ok: true, installments: res.installments });
    } catch (err) {
      setSplitResult({ ok: false, message: err.response?.data?.error || 'Failed to split into installments.' });
    } finally {
      setSplitting(false);
    }
  }

  return (
    <div className="mt-3 rounded-2xl border-2 border-rose-200 bg-rose-50 px-4 py-3 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 shrink-0 rounded-full bg-rose-500 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" className="h-4 w-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-bold text-rose-800">Annual Fees Arrears</p>
            <p className="text-xs text-rose-600">Paid through {fees_year} · {years.length} year{years.length !== 1 ? 's' : ''} outstanding</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-base font-bold text-rose-700">TZS {Number(total_debt).toLocaleString()}</p>
          <button
            onClick={() => setExpanded(v => !v)}
            className="text-[10px] font-semibold text-rose-500 hover:text-rose-700 underline underline-offset-2"
          >
            {expanded ? 'Hide' : 'Show breakdown'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-rose-200 pt-2 space-y-1 max-h-40 overflow-y-auto pr-1">
          {years.map(y => (
            <div key={y.year} className="flex justify-between text-xs text-rose-700">
              <span>{y.year}</span>
              <span className="font-semibold">TZS {Number(y.amount).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}

      {eligibleForInstallment && (
        <div className="border-t border-rose-200 pt-2">
          <button
            onClick={handleSplit}
            disabled={splitting}
            className="rounded-lg bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white px-3 py-1.5 text-xs font-semibold"
          >
            {splitting ? 'Splitting…' : `Split into 2 Installments (>4 years owed)`}
          </button>
          {splitResult?.ok && (
            <div className="mt-2 text-xs text-rose-700 space-y-0.5">
              <p className="font-semibold">Created:</p>
              {splitResult.installments.map(i => (
                <p key={i.invoice_id}>Installment {i.installment}: {i.reference_no} — TZS {Number(i.fee).toLocaleString()}</p>
              ))}
            </div>
          )}
          {splitResult && !splitResult.ok && (
            <p className="mt-2 text-xs text-rose-700">{splitResult.message}</p>
          )}
        </div>
      )}
    </div>
  );
}
